import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';

const AdminUserEdit = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    role: 'user',
    is_active: true,
  });
  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const data = await apiService.getAdminUserById(userId);
      setOriginalData(data);
      setFormData({
        email: data.email || '',
        username: data.username || '',
        password: '',
        role: data.role || 'user',
        is_active: data.is_active !== undefined ? data.is_active : true,
      });
    } catch (err) {
      console.error('Error fetching user details:', err);
      setSubmitError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation (if changed)
    if (formData.email && formData.email !== originalData.email) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    }

    // Username validation (if changed)
    if (formData.username && formData.username !== originalData.username) {
      if (formData.username.length < 3 || formData.username.length > 30) {
        newErrors.username = 'Username must be 3-30 characters long';
      } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
        newErrors.username = 'Username can only contain letters, numbers, hyphens, and underscores';
      }
    }

    // Password validation (if provided)
    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSuccessMessage('');

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      // Build payload with only changed fields
      const payload = {};
      
      if (formData.email !== originalData.email) {
        payload.email = formData.email;
      }
      if (formData.username !== originalData.username) {
        payload.username = formData.username;
      }
      if (formData.password) {
        payload.password = formData.password;
      }
      if (formData.role !== originalData.role) {
        payload.role = formData.role;
      }
      if (formData.is_active !== originalData.is_active) {
        payload.is_active = formData.is_active;
      }

      // Check if any changes were made
      if (Object.keys(payload).length === 0) {
        setSuccessMessage('No changes were made');
        setSubmitting(false);
        return;
      }

      const response = await apiService.updateAdminUser(userId, payload);
      console.log('User updated successfully:', response);
      
      setSuccessMessage(response.message || 'User updated successfully');
      
      // Update original data
      setOriginalData(response.user);
      setFormData({
        email: response.user.email || '',
        username: response.user.username || '',
        password: '',
        role: response.user.role || 'user',
        is_active: response.user.is_active !== undefined ? response.user.is_active : true,
      });

      // Navigate back to detail page after 1.5 seconds
      setTimeout(() => {
        navigate(`/admin/users/${userId}`);
      }, 1500);
    } catch (err) {
      console.error('Error updating user:', err);
      setSubmitError(err.message || 'Failed to update user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading user details...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <button style={styles.backButton} onClick={() => navigate(`/admin/users/${userId}`)}>
              <i className="fas fa-arrow-left"></i>
            </button>
            <div>
              <h1 style={styles.title}>Edit User</h1>
              <p style={styles.subtitle}>{originalData?.email}</p>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.formCard}>
        <form onSubmit={handleSubmit}>
          {submitError && (
            <div style={styles.errorAlert}>
              <i className="fas fa-exclamation-circle" style={styles.alertIcon}></i>
              {submitError}
            </div>
          )}

          {successMessage && (
            <div style={styles.successAlert}>
              <i className="fas fa-check-circle" style={styles.alertIcon}></i>
              {successMessage}
            </div>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              style={{...styles.input, ...(errors.email ? styles.inputError : {})}}
              placeholder="user@example.com"
              disabled={submitting}
            />
            {errors.email && <span style={styles.errorText}>{errors.email}</span>}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="username">
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              style={{...styles.input, ...(errors.username ? styles.inputError : {})}}
              placeholder="johndoe"
              disabled={submitting}
            />
            {errors.username && <span style={styles.errorText}>{errors.username}</span>}
            <span style={styles.helpText}>
              3-30 characters, letters, numbers, hyphens, and underscores only
            </span>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="password">
              Password <span style={styles.optional}>(Optional)</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              style={{...styles.input, ...(errors.password ? styles.inputError : {})}}
              placeholder="Leave empty to keep current password"
              disabled={submitting}
            />
            {errors.password && <span style={styles.errorText}>{errors.password}</span>}
            <span style={styles.helpText}>
              Leave empty to keep current password. Minimum 8 characters if changing.
            </span>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="role">
                Role
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                style={styles.select}
                disabled={submitting}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <span style={styles.helpText}>
                Be careful: Changing your own role may affect your access
              </span>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Status</label>
              <div style={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  style={styles.checkbox}
                  disabled={submitting}
                />
                <label htmlFor="is_active" style={styles.checkboxLabel}>
                  Active User
                </label>
              </div>
              <span style={styles.helpText}>
                Inactive users cannot log in
              </span>
            </div>
          </div>

          <div style={styles.formActions}>
            <button
              type="button"
              style={styles.cancelButton}
              onClick={() => navigate(`/admin/users/${userId}`)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{...styles.submitButton, ...(submitting ? styles.submitButtonDisabled : {})}}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div style={styles.spinner}></div>
                  Updating...
                </>
              ) : (
                <>
                  <i className="fas fa-save" style={styles.buttonIcon}></i>
                  Update User
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: '20px',
    fontFamily: '"Open Sans", sans-serif',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
    gap: '20px',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #ffffff',
    borderTop: '2px solid transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '18px',
    color: '#2c5aa0',
    fontWeight: '500',
  },
  header: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '24px 30px',
    marginBottom: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
  },
  headerContent: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: '1px solid #2c5aa0',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '16px',
    color: '#2c5aa0',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    margin: '0 0 4px 0',
    color: '#2c5aa0',
  },
  subtitle: {
    fontSize: '14px',
    margin: '0',
    color: '#666666',
  },
  formCard: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '32px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
    maxWidth: '800px',
    margin: '0 auto',
  },
  errorAlert: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  successAlert: {
    backgroundColor: '#d4edda',
    color: '#155724',
    border: '1px solid #c3e6cb',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  alertIcon: {
    fontSize: '16px',
  },
  formGroup: {
    marginBottom: '24px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333333',
    marginBottom: '8px',
  },
  optional: {
    color: '#666666',
    fontWeight: '400',
    fontSize: '12px',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontFamily: '"Open Sans", sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  inputError: {
    borderColor: '#dc3545',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontFamily: '"Open Sans", sans-serif',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: 'white',
    boxSizing: 'border-box',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '16px',
    color: '#333333',
    cursor: 'pointer',
    userSelect: 'none',
  },
  errorText: {
    display: 'block',
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '4px',
  },
  helpText: {
    display: 'block',
    color: '#666666',
    fontSize: '12px',
    marginTop: '4px',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #e0e0e0',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#666666',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitButton: {
    backgroundColor: '#2c5aa0',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  submitButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  buttonIcon: {
    fontSize: '14px',
  },
};

export default AdminUserEdit;
